<script>
/**
 * AccountTypeModal — edit a merchant's Account Type (KYB tier).
 *
 * Wired: each dropped/selected file is uploaded directly to S3 via a pre-signed PUT
 * (presignAccountTypeDocument -> HTTP PUT to the returned uploadUrl); Save posts the chosen tier +
 * uploaded documentIds to /merchant/update-account-type (updateAccountType).
 *
 * Flow: pick a tier -> for a non-Micro tier the per-tier document requirements + uploader appear ->
 * Save (disabled until >=1 doc for tiers that require documents).
 *
 * The per-tier requirement NAMES + file constraints come from accountTypeConfig (static config, not mock
 * data) so the modal and the User Documents tab stay consistent.
 */
import { customerLiteMethods } from "@/state/helpers";
import {
  TIER_OPTIONS,
  TIER_LABELS,
  REQUIREMENTS_BY_TIER,
  ACCEPTED_TYPES,
  MAX_FILE_BYTES,
  formatFileSize,
} from "./accountTypeMock";

export default {
  name: "AccountTypeModal",
  props: {
    value: { type: Boolean, default: false }, // v-model visibility
    currentTier: { type: String, default: "" }, // data value: '' | micro | small | medium | corporate
    businessId: { type: [String, Number], default: null }, // ref_business.account_id
  },
  data() {
    return {
      selectedTier: "micro",
      files: [], // { documentId, fileName, sizeBytes, progress, uploading }
      isDragging: false,
      submitting: false,
      tierOptions: TIER_OPTIONS,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    requirements() {
      return REQUIREMENTS_BY_TIER[this.selectedTier] || [];
    },
    requiresDocuments() {
      return this.requirements.length > 0;
    },
    selectedTierLabel() {
      return TIER_LABELS[this.selectedTier] || "Micro";
    },
    attachedCount() {
      return this.files.filter((f) => !f.uploading).length;
    },
    hasPendingUpload() {
      return this.files.some((f) => f.uploading);
    },
    canSave() {
      if (this.submitting || this.hasPendingUpload) return false;
      // Tiers with document requirements need at least one uploaded file.
      if (this.requiresDocuments && this.attachedCount < 1) return false;
      return !!this.selectedTier;
    },
  },
  watch: {
    value(opened) {
      if (opened) {
        this.selectedTier = this.currentTier || "micro";
        this.files = [];
        this.isDragging = false;
        this.submitting = false;
      }
    },
    // Clear any uploaded files when switching tiers so stale docs don't carry over.
    selectedTier() {
      this.files = [];
    },
  },
  methods: {
    ...customerLiteMethods,
    formatSize(bytes) {
      return formatFileSize(bytes);
    },
    onBrowse() {
      this.$refs.fileInput.click();
    },
    onFileInputChange(e) {
      this.addFiles(Array.from(e.target.files || []));
      e.target.value = "";
    },
    onDrop(e) {
      this.isDragging = false;
      this.addFiles(Array.from(e.dataTransfer.files || []));
    },
    addFiles(fileList) {
      for (const file of fileList) {
        if (!ACCEPTED_TYPES.includes(file.type)) continue;
        if (file.size > MAX_FILE_BYTES) continue;
        const entry = {
          documentId: null,
          fileName: file.name,
          sizeBytes: file.size,
          progress: 0,
          uploading: true,
          error: false,
        };
        this.files.push(entry);
        this.uploadFile(file, entry);
      }
    },
    // Direct-to-S3 upload: presign -> PUT the bytes straight to the returned uploadUrl.
    // Use native fetch (NOT axios): the app mutates axios global defaults
    // (axios.defaults.headers = { Authorization: token }), and that bleeds the ERP bearer token into the
    // S3 PUT, so S3 rejects with "Only one auth mechanism allowed". fetch inherits nothing, so the ONLY
    // auth S3 sees is the pre-signed query string. Content-Type must match what the presign signed.
    async uploadFile(file, entry) {
      try {
        const res = await this.presignAccountTypeDocument({
          businessId: this.businessId,
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        });
        if (!res || !res.uploadUrl) {
          throw new Error("Presign returned no uploadUrl.");
        }
        entry.documentId = res.documentId;

        const putRes = await fetch(res.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!putRes.ok) {
          const bodyText = await putRes.text().catch(() => "");
          console.error("S3 PUT rejected:", putRes.status, bodyText);
          throw new Error(`S3 PUT ${putRes.status}`);
        }

        entry.uploading = false;
        entry.progress = 100;
      } catch (err) {
        console.log("Account type document upload failed:", err);
        entry.uploading = false;
        entry.error = true;
        // Drop the failed entry so it can't be submitted.
        const idx = this.files.indexOf(entry);
        if (idx > -1) this.files.splice(idx, 1);
        this.$bvToast &&
          this.$bvToast.toast(`Failed to upload ${file.name}. Please try again.`, {
            title: "Upload failed",
            variant: "danger",
            solid: true,
          });
      }
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    onCancel() {
      this.show = false;
      this.$emit("cancel");
    },
    onSave() {
      if (!this.canSave) return;
      this.submitting = true;
      const documentIds = this.files
        .filter((f) => !f.uploading && f.documentId)
        .map((f) => f.documentId);

      this.updateAccountType({
        businessId: this.businessId,
        accountType: this.selectedTier,
        documentIds,
      })
        .then((message) => {
          this.submitting = false;
          this.$emit("confirmed", {
            tier: this.selectedTier,
            tierLabel: this.selectedTierLabel,
            documents: this.files.map((f) => ({
              documentId: f.documentId,
              fileName: f.fileName,
              sizeBytes: f.sizeBytes,
            })),
            result: message,
          });
          this.show = false;
        })
        .catch((err) => {
          this.submitting = false;
          const msg =
            (err && (err.description || err.message)) ||
            "Unable to update account type. Please try again.";
          this.$emit("failed", msg);
        });
    },
  },
};
</script>

<template>
  <b-modal v-model="show" size="md" centered hide-header hide-footer hide-header-close>
    <div class="text-center">
      <i class="text-info bx bx-info-circle" style="font-size: 90px;"></i>
      <h4 class="fw-semibold text-black my-3">Account Type</h4>
      <p>Please select a account type for this merchant:</p>
    </div>

    <div class="px-3">
      <label class="fw-semibold">Account Type</label>
      <b-form-group class="mb-3">
        <b-form-select
          class="form-select rounded shadow-none"
          v-model="selectedTier"
          :options="tierOptions"
        ></b-form-select>
      </b-form-group>

      <!-- Per-tier document requirements + uploader (only when the tier requires docs) -->
      <template v-if="requiresDocuments">
        <p class="at-reqs-head mb-1">
          Requirements for {{ selectedTierLabel }} Account Type &ndash; Documents:
        </p>
        <ol class="at-reqs mb-3">
          <li v-for="(req, i) in requirements" :key="i">{{ req }}</li>
        </ol>

        <div
          class="at-dropzone"
          :class="{ dragging: isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
          @click="onBrowse"
        >
          <div class="at-dropzone-icon">
            <i class="bx bxs-file-image"></i>
          </div>
          <p class="mb-1 mt-2">
            Drag &amp; drop supporting files here, or
            <a href="javascript:void(0);" class="at-browse" @click.stop="onBrowse">Browse Files</a>
          </p>
          <p class="small text-muted mb-0">
            Supported formats: <b>JPEG, PNG</b> (Multiple files allowed) &middot; Max 15MB per file
          </p>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            accept="image/jpeg,image/png"
            multiple
            @change="onFileInputChange"
          />
        </div>

        <!-- Uploaded Files header is always shown for tiers that require docs (per mockup — "(0 attached)"). -->
        <div class="mt-3">
          <div class="d-flex justify-content-between at-uploaded-head">
            <span class="at-uploaded-label">Uploaded Files:</span>
            <span class="at-uploaded-count">({{ attachedCount }} attached)</span>
          </div>
          <div class="at-file-row" v-for="(file, index) in files" :key="index">
            <i class="bx bxs-file at-file-icon"></i>
            <div class="at-file-body">
              <div class="at-file-name">{{ file.fileName }}</div>
              <div class="small text-muted" v-if="!file.uploading">{{ formatSize(file.sizeBytes) }}</div>
              <div class="at-progress" v-else>
                <div class="at-progress-bar" :style="{ width: file.progress + '%' }"></div>
              </div>
            </div>
            <button type="button" class="at-file-remove" @click="removeFile(index)" aria-label="Remove file">
              <i class="bx" :class="file.uploading ? 'bx-x' : 'bx-trash'"></i>
            </button>
          </div>
          <hr class="at-divider" />
        </div>
      </template>
    </div>

    <div class="text-center mt-3">
      <b-button type="button" class="at-btn at-btn-cancel me-2" :disabled="submitting" @click="onCancel">
        Cancel
      </b-button>
      <b-button type="button" class="at-btn at-btn-save" :disabled="!canSave" @click="onSave">
        {{ submitting ? 'Saving...' : 'Save' }}
      </b-button>
    </div>
  </b-modal>
</template>

<style scoped>
/* Account Type modal — colors sourced from the Figma "Toggle Limit" design tokens. */
.at-reqs-head {
  font-weight: 700;
  font-size: 16px;
  color: #202020;
  letter-spacing: -0.5px;
}
.at-reqs {
  padding-left: 1.1rem;
  font-size: 14px;
  color: #4b4b4b;
}
.at-dropzone {
  border: 2px dashed #c4aacf; /* primary-200 */
  border-radius: 12px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.at-dropzone.dragging {
  background: #fdeaf4; /* secondary-50 */
  border-color: #7f4697; /* primary-500 */
}
.at-dropzone-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fdeaf4; /* secondary-50 */
}
.at-dropzone-icon .bx {
  font-size: 24px;
  color: #e83092; /* secondary-500 */
}
.at-browse {
  color: #7f4697; /* primary-500 */
  font-weight: 700;
  text-decoration: underline;
}
.at-uploaded-head {
  padding: 0 2px;
}
.at-uploaded-label {
  font-weight: 600;
  font-size: 12px;
  color: #202020;
}
.at-uploaded-count {
  font-weight: 600;
  font-size: 12px;
  color: #202020;
}
.at-divider {
  margin: 0.75rem 0 0;
  border: 0;
  border-top: 1px solid #ededed; /* neutral-50 */
  opacity: 1;
}
.at-file-row {
  display: flex;
  align-items: center;
  padding: 0.55rem 0;
}
.at-file-icon {
  font-size: 20px;
  color: #e83092; /* secondary-500 */
  margin-right: 0.6rem;
}
.at-file-body {
  flex: 1;
  min-width: 0;
}
.at-file-name {
  font-weight: 500;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.at-progress {
  height: 6px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
}
.at-progress-bar {
  height: 100%;
  background: #7f4697; /* primary-500 */
  transition: width 0.2s ease;
}
.at-file-remove {
  border: none;
  background: transparent;
  color: #c0392b;
  font-size: 18px;
  cursor: pointer;
}
/* Footer buttons — Cancel neutral-500, Save secondary-500, fixed 110px per mockup. */
.at-btn {
  width: 110px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  letter-spacing: 0.24px;
}
.at-btn-cancel {
  background: #4b4b4b; /* neutral-500 */
  color: #fff;
}
.at-btn-cancel:hover,
.at-btn-cancel:focus {
  background: #3d3d3d;
  color: #fff;
}
.at-btn-save {
  background: #e83092; /* secondary-500 */
  color: #fff;
}
.at-btn-save:hover:not(:disabled),
.at-btn-save:focus:not(:disabled) {
  background: #d12782;
  color: #fff;
}
.at-btn-save:disabled {
  background: #e83092;
  opacity: 0.5;
  color: #fff;
}
</style>
